import { useEffect } from "react";
import CustomInput from "../../../../Components/Atoms/CustomInput";
import CustomButton from "../../../../Components/Atoms/CustomButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import CustomSelect from "../../../../Components/Atoms/CustomSelect";
import {
  getCronReminderEmailsListRequest,
  postCronSettingsRequest,
  postCronSettingsResponseClear,
  putCronSettingsRequest,
} from "../../../../Redux/CronSettings/CronSettingsActions";
import { Autocomplete, TextField } from "@mui/material";
import { getSettingsRequest } from "../../../../Redux/Settings/SettingsActions";

const CreateReminder = ({ handleClose, data }) => {
  const dispatch = useDispatch();

  const { postCronSettingsLoading } = useSelector(
    (state) => state.cronSettingReducer
  );
  const action = data ? "edit" : "new";

  const { getSettingsResponse } = useSelector((state) => state.settingsReducer);

  useEffect(() => {
    dispatch(getSettingsRequest());
  }, []);

  const formik = useFormik({
    initialValues: {
      mode: "TIME",
      status: "ACTIVE",
      hours: 1,
      minutes: 0,
      days: 1,
      emails: [],
      to_number: "",
      notify_type: "EMAIL",
      priority: "LOW",
    },
    onSubmit: (values) => {
      if (postCronSettingsLoading) return;
      const { emails = [] } = values;
      const mod_emails = emails.map((e) => e.email);
      if (action === "new") {
        dispatch(
          postCronSettingsRequest({
            ...values,
            emails: mod_emails,
          })
        );
      } else {
        dispatch(
          putCronSettingsRequest({
            ...values,
            id: data?._id,
            emails: mod_emails,
          })
        );
      }
    },
    validationSchema: Yup.object().shape({
      mode: Yup.string().required("Required"),
      priority: Yup.string().required("Required"),
      status: Yup.string().required("Required"),
      emails: Yup.array().when(["$notify_type"], ([notify_type], schema) => {
        if (["EMAIL", "ALL"].includes(notify_type)) {
          return Yup.array().min(1, "Minimum 1 Email is required");
        } else {
          return schema;
        }
      }),
      to_number: Yup.number().when(
        ["$notify_type"],
        ([notify_type], schema) => {
          if (["SMS", "ALL"].includes(notify_type)) {
            return Yup.number().required("Required");
          } else {
            return schema;
          }
        }
      ),
      hours: Yup.number().when(
        ["$mode", "$minutes"],
        ([mode, minutes], schema) => {
          if (mode === "TIME") {
            return Yup.number()
              .required("Required")
              .min(
                Number(minutes) === 0 ? 1 : 0,
                `Minimum ${Number(minutes) === 0 ? 1 : 0}`
              )
              .max(23, "Maximum 23");
          } else {
            return schema;
          }
        }
      ),
      minutes: Yup.number().when(["$mode"], ([mode], schema) => {
        if (mode === "TIME") {
          return Yup.number()
            .required("Required")
            .min(0, "Minimum 0")
            .max(59, "Maximum 59");
        } else {
          return schema;
        }
      }),
      days: Yup.number().when(["$mode"], ([mode], schema) => {
        if (mode === "DATE") {
          return Yup.number()
            .required("Required")
            .min(1, "Minimum 1")
            .max(45, "Maximum 45");
        } else {
          return schema;
        }
      }),
    }),
  });

  useEffect(() => {
    if (action === "edit") {
      Object.keys(formik.values).forEach((e) => {
        formik.setFieldValue(e, data[e]);
      });
    } else {
      formik.resetForm();
    }
  }, [data]);

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <div className="row">
          <div className="col-md-6">
            <p className="mb-1">Notification Type</p>
            <CustomSelect
              type="text"
              name="notify_type"
              placeholder="SELECT NOTIFICATION TYPE"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.notify_type}
              formik={formik}
              data={[
                { name: "EMAIL", value: "EMAIL" },
                { name: "SMS", value: "SMS" },
                { name: "PUSH NOTIFICATION", value: "WEB_PUSH" },
                { name: "ALL", value: "ALL" },
              ]}
            />
          </div>
          <div className="col-md-6">
            <p className="mb-1">Priority</p>
            <CustomSelect
              type="text"
              name="priority"
              placeholder="SELECT A PRIORITY"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.priority}
              formik={formik}
              data={[
                { name: "LOW", value: "LOW" },
                { name: "HIGH", value: "HIGH" },
              ]}
            />
          </div>
          <div className="col-md-6">
            <p className="mb-1">Status</p>
            <CustomSelect
              type="text"
              name="status"
              placeholder="SELECT A STATUS"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.status}
              formik={formik}
              data={[
                { name: "ACTIVE", value: "ACTIVE" },
                { name: "INACTIVE", value: "INACTIVE" },
              ]}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <p className="mb-1">Mode</p>
            <CustomSelect
              type="text"
              name="mode"
              placeholder="SELECT MODE"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.mode}
              formik={formik}
              data={[
                { name: "TIME", value: "TIME" },
                { name: "DATE", value: "DATE" },
              ]}
            />
          </div>
        </div>
        {formik.values.mode === "TIME" && (
          <div className="row">
            <div className="col-md-6">
              <p className="mb-1">Hour(s)</p>
              <CustomInput
                type="number"
                name="hours"
                placeholder="Enter Hour(s)"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.hours}
                formik={formik}
              />
            </div>
            <div className="col-md-6">
              <p className="mb-1">Minute(s)</p>
              <CustomInput
                type="number"
                name="minutes"
                placeholder="Enter minute(s)"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.minutes}
                formik={formik}
              />
            </div>
          </div>
        )}
        {formik.values.mode === "DATE" && (
          <div className="row">
            <div className="col-md-6">
              <p className="mb-1">Day(s)</p>
              <CustomInput
                type="number"
                name="days"
                placeholder="Enter Day(s)"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.days}
                formik={formik}
              />
            </div>
          </div>
        )}
        {["ALL", "SMS"].includes(formik.values.notify_type) && (
          <div className="row">
            <div className="col-md-6">
              <p className="mb-1">Phone Number</p>
              <CustomInput
                type="number"
                name="to_number"
                placeholder="Enter Phone Number"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.to_number}
                formik={formik}
              />
            </div>
          </div>
        )}
        {["ALL", "EMAIL"].includes(formik.values.notify_type) && (
          // <div className="customInputWrpr">
          //   <p className="mb-1">Emails</p>
          //   <CustomInput
          //     type="text"
          //     multiline
          //     placeholder="Enter email with comma seperated..."
          //     name="emails"
          //     value={formik.values.emails}
          //     onChange={formik.handleChange}
          //     onBlur={formik.handleBlur}
          //     formik={formik}
          //   />
          // </div>
          <div className="customInputWrpr">
            <p className="mb-1">Emails</p>
            <Autocomplete
              name="emails"
              multiple
              isOptionEqualToValue={(option, value) =>
                option.email === value.email
              }
              id="emails"
              options={getSettingsResponse?.data?.emails || []}
              className="w-100"
              getOptionLabel={(option) => option?.email}
              getOptionValue={(option) => option?._id} //
              defaultValue={[]}
              filterSelectedOptions
              size="small"
              limitTags={2}
              onChange={(event, newValue) => {
                formik.setFieldValue("emails", newValue);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.emails || []}
              renderInput={(params) => <TextField {...params} />}
            />
            {formik.touched.emails && formik.errors?.emails && (
              <p className="errMsg">
                {formik.touched.emails && formik.errors?.emails}
              </p>
            )}
          </div>
        )}
        <div className="btnActnBar">
          <CustomButton
            btnType="secondary"
            onClick={() => handleClose()}
            label={"Cancel"}
            action={"CANCEL"}
          />
          <CustomButton
            label={postCronSettingsLoading ? "Loading..." : "Save"}
            type="submit"
            action={"SAVE"}
          />
        </div>
      </form>
    </>
  );
};

export default CreateReminder;
